import type {
  CompetitionPhase,
  CompetitionSeasonView,
  CreateCompetitionSeasonRequest,
  RecordSourceVerificationRequest,
  RuleProfileBlocker,
  RuleProfileDetailView,
  RuleProfileStatus,
  SeasonView,
} from '@stm/contracts';
import { isCalendarDate } from '@stm/contracts';
import type { TranslationKey } from '@stm/i18n';

export const phaseKeys: Readonly<Record<CompetitionPhase, TranslationKey>> = {
  regular: 'competitions.phase.regular',
  playoff: 'competitions.phase.playoff',
  tournament: 'competitions.phase.tournament',
};

export const profileStatusKeys: Readonly<Record<RuleProfileStatus, TranslationKey>> = {
  draft: 'competitions.profile.status.draft',
  active: 'competitions.profile.status.active',
  superseded: 'competitions.profile.status.superseded',
};

export const blockerKeys: Readonly<Record<RuleProfileBlocker, TranslationKey>> = {
  no_sources: 'competitions.blocker.no_sources',
  source_hash_missing: 'competitions.blocker.source_hash_missing',
  sources_not_checked: 'competitions.blocker.sources_not_checked',
  category_mismatch: 'competitions.blocker.category_mismatch',
};

/**
 * The newest day a date the person enters may carry (a source check, the end of a registration).
 * The server holds the exact club-local day; one day of slack keeps a club ahead of UTC from being
 * told "future" for its own today.
 */
export function latestPlausibleDay(): string {
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
  return tomorrow.toISOString().slice(0, 10);
}

const sha256Pattern = /^[0-9a-f]{64}$/;

/** A pasted hash may carry spaces or capitals; the server stores lower-case hexadecimal. */
export function normalizeHash(input: string): string {
  return input.replace(/\s+/g, '').toLowerCase();
}

export function isSha256(candidate: string): boolean {
  return sha256Pattern.test(candidate);
}

export type VerificationProblem = 'hash' | 'date' | 'note' | 'future';

export interface VerificationForm {
  /** Hash text per source id; a blank entry leaves the recorded hash (if any) untouched. */
  hashes: Readonly<Record<string, string>>;
  checkedOn: string;
  note: string;
}

export type VerificationResult =
  | { request: RecordSourceVerificationRequest; problem: null }
  | { request: null; problem: VerificationProblem };

/** Builds the source-check request, or names the first problem so the form can explain it. */
export function evaluateVerification(form: VerificationForm, today: string): VerificationResult {
  const sourceHashes: { sourceId: string; sourceHash: string }[] = [];
  for (const [sourceId, text] of Object.entries(form.hashes)) {
    const sourceHash = normalizeHash(text);
    if (sourceHash === '') continue;
    if (!isSha256(sourceHash)) return { request: null, problem: 'hash' };
    sourceHashes.push({ sourceId, sourceHash });
  }
  if (!isCalendarDate(form.checkedOn)) return { request: null, problem: 'date' };
  if (form.checkedOn > today) return { request: null, problem: 'future' };
  const note = form.note.trim();
  if (note === '') return { request: null, problem: 'note' };
  return { request: { sourceHashes, checkedOn: form.checkedOn, note }, problem: null };
}

export const verificationProblemKeys: Readonly<Record<VerificationProblem, TranslationKey>> = {
  hash: 'competitions.verify.problem.hash',
  date: 'competitions.verify.problem.date',
  future: 'competitions.verify.problem.future',
  note: 'competitions.verify.problem.note',
};

export interface CompetitionForm {
  seasonId: string;
  name: string;
  federation: string;
  category: string;
  division: string;
  format: string;
  phases: readonly CompetitionPhase[];
}

export const emptyCompetitionForm: CompetitionForm = {
  seasonId: '',
  name: '',
  federation: 'FRHG',
  category: '',
  division: '',
  format: '',
  phases: ['regular'],
};

export function competitionRequest(form: CompetitionForm): CreateCompetitionSeasonRequest | null {
  const name = form.name.trim();
  const federation = form.federation.trim();
  if (form.seasonId === '' || name === '' || federation === '' || form.category === '') return null;
  if (form.phases.length === 0) return null;
  return {
    seasonId: form.seasonId,
    name,
    federation,
    category: form.category,
    division: form.division.trim() === '' ? null : form.division.trim(),
    format: form.format.trim() === '' ? null : form.format.trim(),
    phases: [...form.phases],
  };
}

/** Seasons a competition can still be added to: closed seasons are history. */
export function openSeasons(seasons: readonly SeasonView[]): SeasonView[] {
  return seasons.filter((season) => season.status !== 'closed');
}

/** Keeps the competition being looked at while it exists; otherwise the first one. */
export function chooseCompetition(
  competitions: readonly CompetitionSeasonView[],
  current: string | null,
): string | null {
  if (current !== null && competitions.some((competition) => competition.id === current)) {
    return current;
  }
  return competitions[0]?.id ?? null;
}

/** Distinct category codes of the shipped catalog, in catalog order. */
export function catalogCategories(entries: readonly { category: string }[]): string[] {
  return [...new Set(entries.map((entry) => entry.category))];
}

/** The catalog entries that fit a competition's category (and division, when it names one). */
export function catalogEntriesFor<Entry extends { category: string; division: string | null }>(
  entries: readonly Entry[],
  competition: Pick<CompetitionSeasonView, 'category'>,
): Entry[] {
  return entries.filter((entry) => entry.category === competition.category);
}

/** The draft that can still be verified and activated, if the list holds one. */
export function openDraft<Profile extends { status: RuleProfileStatus }>(
  profiles: readonly Profile[],
): Profile | null {
  return profiles.find((profile) => profile.status === 'draft') ?? null;
}

/** A draft that nothing blocks can be activated; everything else is shown, never offered. */
export function canActivate(profile: Pick<RuleProfileDetailView, 'status' | 'blockers'>): boolean {
  return profile.status === 'draft' && profile.blockers.length === 0;
}
